import { refreshAccessToken } from '@goodsteward/gs-auth-spa';
import { useAuthStore } from '@/stores/auth';
import { getCognitoConfig } from './config';

// Refresh loop. Active when there's a refresh token in the store; a no-op
// otherwise. The dev stub IdP doesn't issue refresh tokens (every sign-in
// is a fresh round-trip via /oauth2/authorize), so the loop sits idle in
// dev — `refreshToken` stays null and reschedule() bails out. In prod
// Cognito issues real refresh tokens and the loop drives them.
//
// Schedules a single setTimeout that fires REFRESH_LEAD_MS before the access
// token expires; on success the new token is written back to the store and
// we reschedule. On failure (refresh expired, network down) we log out so
// the next protected route render redirects through the IdP again.
//
// Why proactive *and* reactive: the api/client.ts 401 interceptor also
// attempts a refresh-then-retry. The proactive timer keeps a long-running
// note-entry session from getting interrupted by mid-save 401s; the
// reactive interceptor handles the case where the timer fired but the
// browser tab was suspended and the token expired anyway.

const REFRESH_LEAD_MS = 60_000;

let timer: ReturnType<typeof setTimeout> | null = null;
let unsubscribe: (() => void) | null = null;

export function installRefreshLoop() {
    if (unsubscribe) return; // Already installed.

    const reschedule = () => {
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
        const { token, refreshToken, expiresAt } = useAuthStore.getState();
        if (!token || !refreshToken || !expiresAt) return;

        const lead = Math.max(0, expiresAt - Date.now() - REFRESH_LEAD_MS);
        timer = setTimeout(() => {
            void runRefresh();
        }, lead);
    };

    unsubscribe = useAuthStore.subscribe((s, prev) => {
        if (s.token !== prev.token || s.expiresAt !== prev.expiresAt) {
            reschedule();
        }
    });

    reschedule();
}

export function uninstallRefreshLoop() {
    if (timer) clearTimeout(timer);
    timer = null;
    if (unsubscribe) unsubscribe();
    unsubscribe = null;
}

async function runRefresh() {
    const { refreshToken } = useAuthStore.getState();
    if (!refreshToken) return;
    try {
        const next = await refreshAccessToken(getCognitoConfig(), refreshToken);
        useAuthStore.getState().setCognitoTokens({
            accessToken:  next.accessToken,
            refreshToken: next.refreshToken,
            expiresAt:    next.expiresAt,
        });
    } catch {
        // Refresh failed — token is dead. Drop the session; the next
        // protected route render lands the user on /login.
        useAuthStore.getState().logout();
    }
}

/**
 * Imperative refresh used by the 401 interceptor. Returns the new access
 * token on success, null on failure (caller should bail out). Bails
 * immediately when the store has no refresh token — dev stub-IdP
 * sessions never get one, so the 401 interceptor falls through to
 * logout() instead of pretending to refresh.
 */
export async function refreshNow(): Promise<string | null> {
    const { refreshToken } = useAuthStore.getState();
    if (!refreshToken) return null;
    try {
        const next = await refreshAccessToken(getCognitoConfig(), refreshToken);
        useAuthStore.getState().setCognitoTokens({
            accessToken:  next.accessToken,
            refreshToken: next.refreshToken,
            expiresAt:    next.expiresAt,
        });
        return next.accessToken;
    } catch {
        return null;
    }
}
